import React from 'react';
import SEO from '../components/SEO';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Shield, Users, Clock, Award } from 'lucide-react';
import { companyInfo, faqs } from '../data/mock';

const serviceData = {
  'armed-security': {
    title: 'Armed Security Guards',
    subtitle: 'Professional Armed Protection Services',
    description: 'Our armed security guards provide the highest level of protection for businesses, individuals, and events. Each officer is carefully selected, extensively trained, and licensed to carry firearms.',
    image: 'https://images.pexels.com/photos/14164536/pexels-photo-14164536.jpeg',
    features: [
      'Licensed and bonded armed officers',
      'Extensive firearms training and certification',
      'Background checked with military/law enforcement experience',
      'Tactical response training',
      'Crisis management expertise',
      'Professional appearance and demeanor'
    ],
    benefits: [
      { icon: Shield, title: 'Maximum Deterrence', desc: 'Armed presence significantly reduces criminal activity and threats.' },
      { icon: Users, title: 'Trained Professionals', desc: 'Former military and law enforcement personnel with proven expertise.' },
      { icon: Clock, title: '24/7 Availability', desc: 'Round-the-clock armed security for continuous protection.' },
      { icon: Award, title: 'Fully Certified', desc: 'All officers meet and exceed state licensing requirements.' }
    ],
    useCases: ['High-Value Asset Protection', 'Executive Protection', 'Bank & Financial Institutions', 'Jewelry Stores', 'High-Risk Events', 'Construction Sites with Expensive Equipment']
  },
  'unarmed-security': {
    title: 'Unarmed Security Guards',
    subtitle: 'Professional Unarmed Security Services',
    description: 'Our unarmed security officers provide a professional, approachable security presence. Ideal for environments where armed guards may not be necessary or preferred.',
    image: 'https://images.unsplash.com/photo-1566245024852-04fbf7842ce9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkfGVufDB8fHx8MTc2ODg5MjgxN3ww&ixlib=rb-4.1.0&q=85',
    features: [
      'Professional uniformed officers',
      'Customer service oriented approach',
      'Conflict resolution training',
      'Access control management',
      'Patrol and monitoring services',
      'Incident reporting and documentation'
    ],
    benefits: [
      { icon: Users, title: 'Approachable Presence', desc: 'Friendly yet professional demeanor creates a welcoming environment.' },
      { icon: Shield, title: 'Effective Deterrence', desc: 'Visible security presence prevents incidents before they occur.' },
      { icon: Clock, title: 'Flexible Scheduling', desc: 'Available for any shift or schedule requirements.' },
      { icon: Award, title: 'Cost Effective', desc: 'Professional security at competitive pricing.' }
    ],
    useCases: ['Office Buildings', 'Retail Stores', 'Residential Communities', 'Schools & Universities', 'Healthcare Facilities', 'Corporate Events']
  },
  'fire-watch': {
    title: '24-Hour Fire Watch',
    subtitle: 'Certified Fire Watch Guard Services',
    description: 'Our fire watch guards ensure compliance with fire safety regulations when fire suppression systems are impaired. Available 24/7 for immediate deployment.',
    image: 'https://images.unsplash.com/photo-1618371690240-e0d46eead4b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkfGVufDB8fHx8MTc2ODg5MjgxN3ww&ixlib=rb-4.1.0&q=85',
    features: [
      'OSHA compliant fire watch procedures',
      'Trained in fire prevention and detection',
      'Detailed patrol logs and documentation',
      'Emergency response protocols',
      'Coordination with fire departments',
      'Hot work monitoring capabilities'
    ],
    benefits: [
      { icon: Shield, title: 'Regulatory Compliance', desc: 'Meet all fire marshal and insurance requirements.' },
      { icon: Clock, title: 'Rapid Deployment', desc: 'Guards available within hours of your call.' },
      { icon: Users, title: 'Certified Guards', desc: 'All officers trained in fire safety procedures.' },
      { icon: Award, title: 'Documentation', desc: 'Complete logs for regulatory compliance.' }
    ],
    useCases: ['Construction Sites', 'Sprinkler System Repairs', 'Fire Alarm Maintenance', 'Hot Work Operations', 'Building Renovations', 'Emergency Situations']
  },
  'event-security': {
    title: 'Event Security',
    subtitle: 'Professional Event Security Services',
    description: 'From concerts to corporate events, our event security team ensures the safety of attendees, performers, and staff while maintaining a positive atmosphere.',
    image: 'https://images.unsplash.com/photo-1552739883-6578dc150ac6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxhcm1lZCUyMGd1YXJkfGVufDB8fHx8MTc2ODg5MjgzNHww&ixlib=rb-4.1.0&q=85',
    features: [
      'Crowd management expertise',
      'VIP protection services',
      'Access control and credentialing',
      'Emergency evacuation planning',
      'Bag checks and screening',
      'Parking lot security'
    ],
    benefits: [
      { icon: Users, title: 'Crowd Control', desc: 'Expert management of large gatherings and events.' },
      { icon: Shield, title: 'Risk Mitigation', desc: 'Proactive identification and prevention of potential issues.' },
      { icon: Clock, title: 'Flexible Teams', desc: 'Scalable security presence based on event size.' },
      { icon: Award, title: 'Experience', desc: 'Thousands of successful events secured.' }
    ],
    useCases: ['Concerts & Festivals', 'Corporate Events', 'Sporting Events', 'Weddings & Private Parties', 'Trade Shows', 'Political Rallies']
  },
  'commercial-security': {
    title: 'Commercial Security',
    subtitle: 'Business Security Solutions',
    description: 'Protect your business assets, employees, and customers with our comprehensive commercial security services tailored to your industry.',
    image: 'https://images.unsplash.com/photo-1766150805252-9cd6c37e899f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBzZWN1cml0eXxlbnwwfHx8fDE3Njg4OTI4Mjh8MA&ixlib=rb-4.1.0&q=85',
    features: [
      'Lobby and reception security',
      'Employee and visitor management',
      'Package screening services',
      'After-hours patrol',
      'Emergency response coordination',
      'Loss prevention strategies'
    ],
    benefits: [
      { icon: Shield, title: 'Asset Protection', desc: 'Safeguard your property, inventory, and equipment.' },
      { icon: Users, title: 'Employee Safety', desc: 'Create a secure workplace for your team.' },
      { icon: Clock, title: 'Custom Schedules', desc: 'Security coverage when you need it most.' },
      { icon: Award, title: 'Industry Expertise', desc: 'Specialized knowledge for your business sector.' }
    ],
    useCases: ['Office Buildings', 'Warehouses', 'Manufacturing Plants', 'Data Centers', 'Corporate Headquarters', 'Industrial Facilities']
  }
};

const ServiceDetailPage = () => {
  const { serviceType } = useParams();
  const service = serviceData[serviceType] || serviceData['armed-security'];

  return (
    <>
      <SEO 
        title={service.title}
        description={service.description}
        keywords={`${service.title}, security services, professional security guards, ${serviceType} security`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={service.image} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900/80"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <p className="text-orange-500 font-medium mb-4">{service.subtitle}</p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">{service.title}</h1>
            <p className="text-xl text-gray-300 mb-8">{service.description}</p>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-bold transition-all"
              >
                Get a Free Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a 
                href={`tel:${companyInfo.phone.replace(/[^0-9]/g, '')}`}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-bold transition-all border border-white/20"
              >
                <Phone className="w-5 h-5" />
                {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-8">Service Features</h2>
              <div className="grid gap-4">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 bg-slate-900 rounded-lg p-4">
                    <CheckCircle className="w-6 h-6 text-orange-500 flex-shrink-0" />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src={service.image} 
                alt={service.title}
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Why Choose Our {service.title}?</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.benefits.map((benefit) => (
              <div key={benefit.title} className="bg-slate-800 rounded-xl p-6 text-center hover:bg-slate-750 transition-colors">
                <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Ideal For</h2>
            <p className="text-gray-400">Our {service.title.toLowerCase()} are perfect for these applications</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {service.useCases.map((useCase) => (
              <span 
                key={useCase}
                className="bg-slate-900 text-gray-300 px-6 py-3 rounded-full border border-slate-700 hover:border-orange-500 transition-colors"
              >
                {useCase}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.slice(0, 5).map((faq) => (
              <details key={faq.id} className="group bg-slate-800 rounded-lg">
                <summary className="flex items-center justify-between p-5 cursor-pointer text-white font-medium">
                  {faq.question}
                  <span className="text-orange-500 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-5 pb-5 text-gray-400">{faq.answer}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-white/80 mb-8">Contact us today for a free consultation and customized security plan.</p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-orange-500 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
          >
            Request a Quote
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default ServiceDetailPage;